import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Toaster } from "./components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import EmployeesPage from "./pages/EmployeesPage";
import EmployeeDetailPage from "./pages/EmployeeDetailPage";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import TasksPage from "./pages/TasksPage";
import TimeTrackingPage from "./pages/TimeTrackingPage";
import MarketplacePage from "./pages/MarketplacePage";
import PaymentsPage from "./pages/PaymentsPage";
import ClientsPage from "./pages/ClientsPage";
import ClientDetailPage from "./pages/ClientDetailPage";
import InvoicesPage from "./pages/InvoicesPage";
import QuotationsPage from "./pages/QuotationsPage";
import ServicesPage from "./pages/ServicesPage";
import AccountingPage from "./pages/AccountingPage";
import StakeholdersPage from "./pages/StakeholdersPage";
import LandingPage from "./pages/LandingPage";
import ReportsPage from "./pages/ReportsPage";
import BankPage from "./pages/BankPage";
import ClientPortalPage from "./pages/ClientPortalPage";
import UsersPage from "./pages/UsersPage";
import QuotationRequestsPage from "./pages/QuotationRequestsPage";
import CapacityPlanningPage from "./pages/CapacityPlanningPage";
import SubscriptionsPage from "./pages/SubscriptionsPage";
import TimesheetApprovalPage from "./pages/TimesheetApprovalPage";
import BillingDashboardPage from "./pages/BillingDashboardPage";
import AuditLogPage from "./pages/AuditLogPage";
import VacationsPage from "./pages/VacationsPage";
import SuppliersPage from "./pages/SuppliersPage";
import CRMPage from "./pages/CRMPage";
import CompaniesPage from "./pages/CompaniesPage";
import WorkflowsPage from "./pages/WorkflowsPage";
import InvoiceRemindersSettingsPage from "./pages/InvoiceRemindersSettingsPage";
import NotificationPreferencesPage from "./pages/NotificationPreferencesPage";

// IT Operations Pages
import ITDashboardPage from "./pages/it-operations/ITDashboardPage";
import ITAssetsPage from "./pages/it-operations/ITAssetsPage";
import ITAssetDetailPage from "./pages/it-operations/ITAssetDetailPage";
import ServiceVisitsPage from "./pages/it-operations/ServiceVisitsPage";
import ServiceVisitDetailPage from "./pages/it-operations/ServiceVisitDetailPage";
import ClientLocationsPage from "./pages/it-operations/ClientLocationsPage";
import TicketsPage from "./pages/it-operations/TicketsPage";
import MaintenancePage from "./pages/it-operations/MaintenancePage";
import ContractsPage from "./pages/it-operations/ContractsPage";
import SparePartsPage from "./pages/it-operations/SparePartsPage";
import ITReportsPage from "./pages/it-operations/ITReportsPage";
import ServiceVisitReportPage from "./pages/it-operations/ServiceVisitReportPage";
import ExpenseReportPage from "./pages/ExpenseReportPage";
import TaxReportsPage from "./pages/TaxReportsPage";

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            {/* Accounts are created and passwords reset only by an admin: old links go to the login */}
            <Route path="/register" element={<Navigate to="/login" replace />} />
            <Route path="/forgot-password" element={<Navigate to="/login" replace />} />
            <Route path="/reset-password" element={<Navigate to="/login" replace />} />
            <Route path="/gasto" element={<ExpenseReportPage />} />
            
            {/* Protected routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } />
            
            <Route path="/employees" element={
              <ProtectedRoute allowedRoles={['admin', 'rrhh', 'gerencia', 'pm']}>
                <EmployeesPage />
              </ProtectedRoute>
            } />
            
            <Route path="/employees/:employeeId" element={
              <ProtectedRoute allowedRoles={['admin', 'rrhh', 'gerencia', 'pm']}>
                <EmployeeDetailPage />
              </ProtectedRoute>
            } />
            
            <Route path="/users" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <UsersPage />
              </ProtectedRoute>
            } />
            
            <Route path="/projects" element={
              <ProtectedRoute>
                <ProjectsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/projects/:projectId" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia', 'pm', 'empleado', 'cliente']}>
                <ProjectDetailPage />
              </ProtectedRoute>
            } />
            
            <Route path="/tasks" element={
              <ProtectedRoute>
                <TasksPage />
              </ProtectedRoute>
            } />
            
            <Route path="/time-tracking" element={
              <ProtectedRoute>
                <TimeTrackingPage />
              </ProtectedRoute>
            } />
            
            <Route path="/marketplace" element={
              <ProtectedRoute>
                <MarketplacePage />
              </ProtectedRoute>
            } />
            
            <Route path="/payments" element={
              <ProtectedRoute>
                <PaymentsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/clients" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia', 'pm']}>
                <ClientsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/clients/:clientId" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia', 'pm']}>
                <ClientDetailPage />
              </ProtectedRoute>
            } />
            
            <Route path="/invoices" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia']}>
                <InvoicesPage />
              </ProtectedRoute>
            } />
            
            <Route path="/quotations" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia']}>
                <QuotationsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/services" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia']}>
                <ServicesPage />
              </ProtectedRoute>
            } />
            
            <Route path="/quotation-requests" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia', 'pm']}>
                <QuotationRequestsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/tax-reports" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia']}>
                <TaxReportsPage />
              </ProtectedRoute>
            } />

            <Route path="/accounting" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia']}>
                <AccountingPage />
              </ProtectedRoute>
            } />
            
            <Route path="/stakeholders" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia']}>
                <StakeholdersPage />
              </ProtectedRoute>
            } />
            
            <Route path="/reports" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia', 'pm']}>
                <ReportsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/capacity-planning" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia', 'pm']}>
                <CapacityPlanningPage />
              </ProtectedRoute>
            } />
            
            <Route path="/subscriptions" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia', 'pm']}>
                <SubscriptionsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/bank" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia']}>
                <BankPage />
              </ProtectedRoute>
            } />
            
            <Route path="/client-portal" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia', 'cliente']}>
                <ClientPortalPage />
              </ProtectedRoute>
            } />
            
            <Route path="/timesheet-approval" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia', 'pm']}>
                <TimesheetApprovalPage />
              </ProtectedRoute>
            } />
            
            <Route path="/billing-dashboard" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia']}>
                <BillingDashboardPage />
              </ProtectedRoute>
            } />
            
            <Route path="/audit-log" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia']}>
                <AuditLogPage />
              </ProtectedRoute>
            } />
            
            <Route path="/vacations" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia', 'rrhh', 'pm', 'empleado']}>
                <VacationsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/suppliers" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia', 'pm']}>
                <SuppliersPage />
              </ProtectedRoute>
            } />

            <Route path="/crm" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia', 'pm', 'ventas']}>
                <CRMPage />
              </ProtectedRoute>
            } />

            <Route path="/companies" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia']}>
                <CompaniesPage />
              </ProtectedRoute>
            } />

            <Route path="/workflows" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia', 'pm', 'rrhh']}>
                <WorkflowsPage />
              </ProtectedRoute>
            } />

            <Route path="/invoice-reminders" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia']}>
                <InvoiceRemindersSettingsPage />
              </ProtectedRoute>
            } />

            <Route path="/notification-preferences" element={
              <ProtectedRoute>
                <NotificationPreferencesPage />
              </ProtectedRoute>
            } />

            {/* IT Operations Routes */}
            <Route path="/it/dashboard" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia', 'pm', 'tecnico']}>
                <ITDashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/it/assets" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia', 'pm', 'tecnico', 'empleado']}>
                <ITAssetsPage />
              </ProtectedRoute>
            } />
            <Route path="/it/assets/:assetId" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia', 'pm', 'tecnico', 'empleado']}>
                <ITAssetDetailPage />
              </ProtectedRoute>
            } />
            <Route path="/it/visits" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia', 'pm', 'tecnico', 'empleado']}>
                <ServiceVisitsPage />
              </ProtectedRoute>
            } />
            <Route path="/it/visits/:visitId" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia', 'pm', 'tecnico', 'empleado']}>
                <ServiceVisitDetailPage />
              </ProtectedRoute>
            } />
            <Route path="/it/locations" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia', 'pm', 'tecnico']}>
                <ClientLocationsPage />
              </ProtectedRoute>
            } />
            {/* IT quotes and payments were merged into the regular pages */}
            <Route path="/it/quotes" element={<Navigate to="/quotations" replace />} />
            <Route path="/it/payments" element={<Navigate to="/payments" replace />} />
            <Route path="/it/tickets" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia', 'pm', 'tecnico', 'empleado', 'cliente']}>
                <TicketsPage />
              </ProtectedRoute>
            } />
            <Route path="/it/maintenance" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia', 'pm', 'tecnico']}>
                <MaintenancePage />
              </ProtectedRoute>
            } />
            <Route path="/it/contracts" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia', 'pm', 'tecnico']}>
                <ContractsPage />
              </ProtectedRoute>
            } />
            <Route path="/it/spare-parts" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia', 'pm', 'tecnico']}>
                <SparePartsPage />
              </ProtectedRoute>
            } />
            <Route path="/it/reports" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia', 'pm', 'tecnico']}>
                <ITReportsPage />
              </ProtectedRoute>
            } />
            <Route path="/it/visits/:visitId/report" element={
              <ProtectedRoute allowedRoles={['admin', 'gerencia', 'pm', 'tecnico', 'empleado']}>
                <ServiceVisitReportPage />
              </ProtectedRoute>
            } />

            {/* Default redirect */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster 
          position="top-right" 
          richColors 
          toastOptions={{
            style: {
              background: '#151B26',
              border: '1px solid #242E42',
              color: '#F3F4F6',
            },
          }}
        />
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
